/**
 * Kanallar (panel, read only, 28 Oct 2026): GET /channels. Etsy, the site, YouTube, Pinterest and CGTrader on one page,
 * each with its last read, a few headline numbers, its best items and a 30 day series of its main number; under them
 * real revenue month by month against the one year forecast (twin.revenue_forecast).
 */
import { z } from 'zod';
import { contributionSchema } from './ledger.js';
export const CHANNEL_IDS = ['etsy', 'site', 'youtube', 'pinterest', 'cgtrader'];
/** ok: read recently; waiting: connected, first read not yet; not_connected: a key or a login is missing; error. */
export const channelStateSchema = z.enum(['ok', 'waiting', 'not_connected', 'error']);
export const channelMetricSchema = z.object({
    label: z.string(),
    value: z.number().nullable(),
    unit: z.enum(['count', 'usd']),
    /** The change over the last 30 days where two reads exist, else null. */
    change30: z.number().nullable(),
});
export const channelItemSchema = z.object({
    title: z.string(),
    url: z.string().nullable(),
    metrics: z.array(z.object({ label: z.string(), value: z.number().nullable() })),
});
/** v0.1.265: the Pinterest pin queue on the Pinterest card (absent on the other channels and in older answers). */
export const pinPublishingSchema = z.object({
    queued: z.number().int(),
    publishedToday: z.number().int(),
    dailyLimit: z.number().int(),
    lastPublishedAt: z.number().int().nullable(),
    environment: z.enum(['sandbox', 'production']),
    /** "Deneme ortamı (Trial): pinler yalnız bize görünür" under Trial access. */
    environmentText: z.string(),
    /** Why queued pins wait (no pins:write, a 429 back-off ...), null when nothing holds them. */
    waitingText: z.string().nullable(),
    skipped7d: z.number().int(),
    failed7d: z.number().int(),
});
export const channelSummarySchema = z.object({
    id: z.enum(CHANNEL_IDS),
    name: z.string(),
    url: z.string().nullable(),
    state: channelStateSchema,
    stateText: z.string(),
    /** Where Sercan fixes a missing connection (a panel path), null when nothing is missing. */
    fixPath: z.string().nullable(),
    readAt: z.number().int().nullable(),
    metrics: z.array(channelMetricSchema),
    itemsTitle: z.string(),
    items: z.array(channelItemSchema),
    seriesLabel: z.string(),
    series: z.array(z.object({ day: z.string(), value: z.number() })),
    publishing: pinPublishingSchema.optional(),
});
export const revenueMonthSchema = z.object({
    /** YYYY-MM */
    month: z.string().regex(/^\d{4}-\d{2}$/),
    label: z.string(),
    /** Etsy and site orders that count as revenue (not cancelled or refunded), null for a month still to come. */
    ordersCents: z.number().int().nullable(),
    orders: z.number().int().nullable(),
    /** CGTrader earnings of the month where read, else null. */
    cgtraderCents: z.number().int().nullable(),
    /** The forecast's expected case, null outside the forecast year. */
    expectedCents: z.number().int().nullable(),
    /** The running month: the actual figure is partial. */
    running: z.boolean(),
});
export const channelsViewSchema = z.object({
    asOf: z.number().int(),
    channels: z.array(channelSummarySchema),
    revenue: z.object({
        months: z.array(revenueMonthSchema),
        /** One line: where the running month stands against its expected case, pro rata. */
        summary: z.string(),
        note: z.string(),
    }),
    /** v0.1.254: the contribution of the last 7 and 28 days (GET /ledger has the orders); absent in older answers. */
    contribution: z.object({ week: contributionSchema, month: contributionSchema, feeNote: z.string() }).optional(),
});
//# sourceMappingURL=channels.js.map